import React from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { toggleModal } from 'apps/auth/actions/appAction';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelContent, PanelFrame, PanelLeftBlock, PanelRightBlock, PanelRow } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import ActivityIcon from 'apps/auth/components/ga/activity/components/ActivityIcon';
import { PROVIDER_ID_FROM_P2P, PROVIDER_ID_TO_P2P, STATUS_NEED_TO_PASS_KYC, STATUS_OK } from 'apps/auth/constants/Constants';
import {
    AGENT_TO_LIBRE,
    CASH_WITHDRAWAL,
    CREDIT,
    DEBIT,
    DELIVERY,
    EXCHANGE,
    INTERNAL_TRANSFER_OUT,
    OFFLINE_PAYMENT,
    ONLINE_PAYMENT,
    TOP_UP,
    TRANSFER_FROM_GT,
    UNKNOWN,
} from 'apps/auth/constants/GaConstants';
import { AUTH_GA_STUB_PAGE } from 'apps/auth/constants/UrlConstants';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { deleteArtifactsFromStr, toUpperCase } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import media from 'apps/auth/theme/media';

import ActivityDetailsModal from './modals/ActivityDetailsModal';

export const ActivityItemSubheader = styled.div`
    margin-top: 48px;

    ${media.desktop1920} {
        margin-top: 72px;
    }
`;

const ActivityItem = (props) => {
    const messages = usePageMessages();
    const extra = useExtra();
    const { country } = useApp();
    const { lang } = useLanguage();

    const item = props.item;
    const pocketId = extra.pocket?.id;
    const mobileInfo = props.item.mobileInfo;
    const icon = mobileInfo.icon;
    const isP2P =
        (item.frPrvId === PROVIDER_ID_FROM_P2P || item.frPrvId === PROVIDER_ID_TO_P2P) &&
        (item.toPrvId === PROVIDER_ID_FROM_P2P || item.toPrvId === PROVIDER_ID_TO_P2P);
    const gtStsId = item.extra?.gt_sts_id && Number(item.extra.gt_sts_id);
    const stsId = gtStsId ? gtStsId : item.mobileInfo?.stsId;
    const failedCheque = gtStsId ? gtStsId < 10 || gtStsId > STATUS_OK : stsId > STATUS_OK;
    const textColor = failedCheque ? 'lightGray' : 'primary';
    const titleColor = failedCheque ? 'lightGray' : 'secondary';
    const options = {
        titleColor,
        failedCheque,
        textColor,
        stsId,
    };

    const libreReceiveMoney =
        item.patternId === AGENT_TO_LIBRE &&
        (((!extra.card || extra.needInfoAboutBank) && mobileInfo.stsId === STATUS_OK) || mobileInfo.stsId === STATUS_NEED_TO_PASS_KYC);

    const date = new Date(props.date);

    const getStatusIcon = (status) => {
        if (status < STATUS_OK || libreReceiveMoney) {
            return <Icon name="clock-filled" color="secondary" size="16px" leftIndent="8px" />;
        } else if (status === STATUS_OK) {
            return <Icon name="check-rounded" color="primary" size="16px" leftIndent="8px" />;
        } else if (status > STATUS_OK) {
            return <Icon name="cross-rounded" color="secondary" size="16px" leftIndent="8px" />;
        }
        return null;
    };
    const getGtStatusIcon = (status) => {
        if (status >= 10 && status < STATUS_OK) {
            return <Icon name="clock-filled" color="secondary" size="16px" leftIndent="8px" />;
        } else if (status === STATUS_OK) {
            return <Icon name="check-rounded" color="primary" size="16px" leftIndent="8px" />;
        }
        return <Icon name="cross-rounded" color="secondary" size="16px" leftIndent="8px" />;
    };

    const getPayDetailsTop = () => {
        if ((icon === TOP_UP && !(item.patternId === AGENT_TO_LIBRE)) || icon === EXCHANGE || icon === DELIVERY) {
            return false;
        } else if (isP2P || TRANSFER_FROM_GT.includes(icon)) {
            return <>{getPayDescription(icon, mobileInfo.extraFields)}</>;
        } else {
            return <>{getPayDescription(icon, mobileInfo.extraFields)}</>;
        }
    };

    const getPayDetailsBottom = () => {
        if ((icon === TOP_UP && !(item.patternId === AGENT_TO_LIBRE)) || icon === EXCHANGE || icon === DELIVERY) {
            return false;
        } else if (isP2P || TRANSFER_FROM_GT.includes(icon)) {
            return <>{gtStsId ? getGtStatusIcon(stsId) : getStatusIcon(stsId)}</>;
        } else if (libreReceiveMoney) {
            return <Badge caption={messages.receiveButton} />;
        } else {
            return null;
        }
    };

    const getPayDescription = (currIcon, extraFields = {}) => {
        let comment;
        if (TRANSFER_FROM_GT.includes(currIcon)) {
            comment = mobileInfo.direction === DEBIT ? extraFields.Delivery : extraFields.Transfer;
        } else if (item.frPrvId === PROVIDER_ID_FROM_P2P || item.frPrvId === PROVIDER_ID_TO_P2P || item.patternId === AGENT_TO_LIBRE) {
            comment = item.payComment;
        }
        return (
            <Text size="secondary" color="secondary" variant="div" ellipsis style={{ marginTop: '4px' }}>
                {comment ? <>{comment}</> : null}
            </Text>
        );
    };

    const getSymbolForPlus = (id) => {
        return id > STATUS_OK ? '' : '+ ';
    };

    const getSymbolForMinus = (id) => {
        return id > STATUS_OK ? '' : '- ';
    };

    const getExchangeAmount = (currItem) => {
        const isExchangeFromCurrAccount = extra.pocket?.pocketCurrency?.code === mobileInfo.frCurIso;
        const symbolFrom = isExchangeFromCurrAccount ? '-' : '+';
        const symbolTo = isExchangeFromCurrAccount ? '+' : '-';
        const colorFrom = 'primary';
        const amountFrom = isExchangeFromCurrAccount ? currItem?.frAmtReal : currItem?.toAmtReal;
        const amountTo = isExchangeFromCurrAccount ? currItem?.toAmtReal : currItem?.frAmtReal;
        const currencyFrom = isExchangeFromCurrAccount ? mobileInfo?.frCurIso : mobileInfo?.toCurIso;
        const currencyTo = isExchangeFromCurrAccount ? mobileInfo?.toCurIso : mobileInfo?.frCurIso;
        const getColorTo = () => {
            let color;
            if (isExchangeFromCurrAccount) {
                color = 'primary';
            } else {
                color = stsId > STATUS_OK ? 'lightGray' : 'primary';
            }
            return color;
        };
        const sumFrom = (
            <Text color={colorFrom} variant="div">
                {symbolFrom}
                {getLocalizedRawAmount(lang, country, amountFrom) + ' '}
                <Text color={colorFrom} smallCaps>
                    {currencyFrom.toLowerCase()}
                </Text>
            </Text>
        );

        const sumTo = (
            <Text size="secondary" color={getColorTo()} variant="div" lineThrough={stsId > STATUS_OK}>
                {symbolTo} {getLocalizedRawAmount(lang, country, amountTo) + ' '}
                <Text color={getColorTo()} smallCaps>
                    {currencyTo.toLowerCase()}
                </Text>
            </Text>
        );
        return (
            <>
                {sumFrom}
                {sumTo}
            </>
        );
    };

    // status?.let {
    //     when {
    //         status == 83 -> Status.WAITING_KYC
    //         status < 90 -> Status.WAITING
    //         status == 90 -> Status.PAYED
    //         status == 183 -> Status.EXPIRED
    //         status == 210 -> Status.REFUND
    //         status == 220 -> Status.REVERSED
    //         status > 90 -> Status.ERROR
    //     else -> Status.ALL
    //     }
    const getTextForAmount = (currItem, getSymbol = getSymbolForMinus, color = 'primary') => {
        return (
            <Text variant="div" color={color} lineThrough={failedCheque}>
                {getSymbol?.(mobileInfo.stsId)}
                {getLocalizedRawAmount(lang, country, currItem?.frAmtReal) + ' '}
                <Text smallCaps color={color}>
                    {item.patternId === AGENT_TO_LIBRE ? mobileInfo?.toCurIso.toLowerCase() : mobileInfo?.frCurIso.toLowerCase()}
                </Text>
            </Text>
        );
    };

    const getAmount = () => {
        if (mobileInfo.direction === CREDIT) {
            const color = stsId !== STATUS_OK || libreReceiveMoney ? 'lightGray' : 'primary';
            return getTextForAmount(item, getSymbolForPlus, color);
        } else if (icon === EXCHANGE) {
            return getExchangeAmount(item);
        } else if (icon === DELIVERY || icon === INTERNAL_TRANSFER_OUT || icon === UNKNOWN || TRANSFER_FROM_GT.includes(icon)) {
            return getTextForAmount(item, getSymbolForMinus, textColor);
        } else if (icon === CASH_WITHDRAWAL || icon === ONLINE_PAYMENT || icon === OFFLINE_PAYMENT) {
            return (
                <>
                    {getTextForAmount(item, getSymbolForMinus, textColor)}
                    {mobileInfo.curRate && mobileInfo.curRate !== 1 && (
                        <Text size="secondary" color="secondary" variant="div" lineThrough={failedCheque}>
                            {getLocalizedRawAmount(lang, country, item.toAmtReal) + ' '}
                            <Text color="secondary" smallCaps>
                                {mobileInfo.toCurIso.toLowerCase()}
                            </Text>
                        </Text>
                    )}
                </>
            );
        } else {
            return getTextForAmount(item, null, textColor);
        }
    };

    const getDate = () => {
        const month = messages[`month_${date.getMonth() + 1}`];
        const weekday = messages[`weekday_${date.getDay() + 1}`];

        return (
            <ActivityItemSubheader>
                <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                    {`${date.getDate()} ${month}`}
                    <span>{' · '}</span>
                    {deleteArtifactsFromStr(toUpperCase(weekday))}
                </Text>
            </ActivityItemSubheader>
        );
    };

    const getFlagName = (currItem) => {
        if (isP2P || item.patternId === AGENT_TO_LIBRE) {
            return 'paysend';
        } else if (currItem.toCountryISO && TRANSFER_FROM_GT.includes(icon)) {
            return `${currItem.toCountryISO.toLowerCase()}`;
        }
        return '';
    };

    const getTitle = () => {
        if (item.patternId === AGENT_TO_LIBRE) {
            return (
                (item.extra?.sender_first_name ? item.extra.sender_first_name + ' ' : '') + (item.extra?.sender_last_name ? item.extra?.sender_last_name : '')
            );
        }
        return mobileInfo.title;
    };

    return (
        <li>
            {props.isShowDate && getDate()}
            <Panel
                type="reactLink"
                to={AUTH_GA_STUB_PAGE}
                onClick={(e) => {
                    if (!item.mobileInfo.icon.includes('grow_')) {
                        e.preventDefault();
                        props.toggleModal(true, <ActivityDetailsModal pocketId={pocketId} item={item} options={options} />, false);
                    }
                }}
                isHoverable
                negativeMargin
            >
                <PanelFrame>
                    <DiskBadgeWrapper>
                        <ActivityIcon item={item} options={options} diskSize="small" flagName={getFlagName(item)} />
                    </DiskBadgeWrapper>
                    <PanelContent>
                        <PanelRow>
                            <PanelLeftBlock>
                                <Text ellipsis color={failedCheque ? 'lightGray' : 'primary'} variant="div">
                                    {getTitle()}
                                </Text>
                                {getPayDetailsTop()}
                            </PanelLeftBlock>
                            <PanelRightBlock>
                                {getAmount()}
                                {getPayDetailsBottom()}
                            </PanelRightBlock>
                        </PanelRow>
                    </PanelContent>
                </PanelFrame>
            </Panel>
        </li>
    );
};

export default connect(null, { toggleModal })(ActivityItem);
